import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Eye,
  Edit,
  BookOpen,
  Filter,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  ThumbsUp,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { cn, formatDate } from '@/lib/utils';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';

interface KBArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string | null;
  category?: {
    id: string;
    name: string;
    slug: string;
  };
  tags: string[] | null;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  isFeatured: boolean;
  viewCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  publishedAt: string | null;
  author?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface KBCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
}

export default function KnowledgeBasePage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('category') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [showFilters, setShowFilters] = useState(false);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    if (search) params.set('search', search);
    if (categoryFilter) params.set('category', categoryFilter);
    if (statusFilter) params.set('status', statusFilter);
    setSearchParams(params);
  }, [page, search, categoryFilter, statusFilter, setSearchParams]);

  // Fetch articles
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['kbArticles', { page, search, categoryFilter, statusFilter }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (categoryFilter) params.append('categoryId', categoryFilter);
      if (statusFilter) params.append('status', statusFilter);
      const response = await api.get(`/knowledge-base/articles?${params.toString()}`);
      return response.data;
    },
  });

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['kbCategories'],
    queryFn: async () => {
      const response = await api.get('/knowledge-base/categories');
      return response.data.data;
    },
  });

  // Toggle publish mutation
  const togglePublishMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'PUBLISHED' | 'DRAFT' }) => {
      return api.put(`/knowledge-base/articles/${id}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kbArticles'] });
      toast({ title: 'Success', description: 'Article status updated' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update article status', variant: 'destructive' });
    },
  });

  const articles: KBArticle[] = data?.data || [];
  const pagination = data?.pagination;
  const categories: KBCategory[] = categoriesData || [];

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setStatusFilter('');
    setPage(1);
  };

  const hasActiveFilters = search || categoryFilter || statusFilter;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return 'bg-green-100 text-green-800';
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800';
      case 'ARCHIVED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const toggleArticleStatus = (article: KBArticle) => {
    const newStatus = article.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    togglePublishMutation.mutate({ id: article.id, status: newStatus });
  };

  const getHelpfulPercentage = (article: KBArticle) => {
    const total = article.helpfulCount + article.notHelpfulCount;
    if (total === 0) return null;
    return Math.round((article.helpfulCount / total) * 100);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Knowledge Base</h1>
          <p className="text-muted-foreground">
            Manage knowledge base articles
            {pagination && ` • ${pagination.total} articles`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button asChild>
            <Link to="/knowledge-base/new">
              <Plus className="w-4 h-4 mr-2" />
              Create Article
            </Link>
          </Button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search articles by title or content..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[160px]"
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <Button 
                variant={showFilters ? "secondary" : "outline"} 
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                More Filters
              </Button>
            </div>

            {showFilters && (
              <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t">
                <select
                  className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[140px]"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="PUBLISHED">Published</option>
                  <option value="DRAFT">Draft</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
                {hasActiveFilters && (
                  <Button variant="ghost" onClick={clearFilters}>
                    Clear All Filters
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Articles Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No articles found</p>
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters 
                  ? "Try adjusting your filters"
                  : "Create your first article to get started"}
              </p>
              {hasActiveFilters ? (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              ) : (
                <Button asChild>
                  <Link to="/knowledge-base/new">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Article
                  </Link>
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Title</th>
                    <th className="text-left p-4 font-medium">Category</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Views</th>
                    <th className="text-left p-4 font-medium">Helpful</th>
                    <th className="text-left p-4 font-medium">Author</th>
                    <th className="text-left p-4 font-medium">Updated</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {articles.map((article) => {
                    const helpfulPct = getHelpfulPercentage(article);
                    return (
                      <tr key={article.id} className="hover:bg-muted/50 transition-colors">
                        <td className="p-4">
                          <div className="max-w-[300px]">
                            <Link 
                              to={`/knowledge-base/${article.id}`}
                              className="font-medium hover:text-primary line-clamp-1"
                            >
                              {article.title}
                            </Link>
                            {article.tags && article.tags.length > 0 && (
                              <div className="flex gap-1 mt-1 flex-wrap">
                                {article.tags.slice(0, 3).map((tag, idx) => (
                                  <span key={idx} className="text-xs bg-muted px-2 py-0.5 rounded">
                                    {tag}
                                  </span>
                                ))}
                                {article.tags.length > 3 && (
                                  <span className="text-xs text-muted-foreground">
                                    +{article.tags.length - 3}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-sm">
                            {article.category?.name || '-'}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={cn(
                            "px-2 py-1 text-xs font-medium rounded-full",
                            getStatusBadge(article.status)
                          )}>
                            {article.status}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-sm text-muted-foreground">
                            {article.viewCount.toLocaleString()}
                          </span>
                        </td>
                        <td className="p-4">
                          {helpfulPct !== null ? (
                            <div className="flex items-center gap-2">
                              <ThumbsUp className={cn(
                                "w-4 h-4",
                                helpfulPct >= 70 ? "text-green-600" : 
                                helpfulPct >= 50 ? "text-yellow-600" : "text-red-600"
                              )} />
                              <span className="text-sm">{helpfulPct}%</span>
                              <span className="text-xs text-muted-foreground">
                                ({article.helpfulCount + article.notHelpfulCount})
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-4">
                          {article.author ? (
                            <span className="text-sm">
                              {article.author.firstName} {article.author.lastName}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {formatDate(article.updatedAt)}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => toggleArticleStatus(article)}
                              title={article.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
                              disabled={togglePublishMutation.isPending}
                            >
                              {article.status === 'PUBLISHED' ? (
                                <ToggleRight className="w-4 h-4 text-green-600" />
                              ) : (
                                <ToggleLeft className="w-4 h-4 text-muted-foreground" />
                              )}
                            </Button>
                            <Button variant="ghost" size="icon" asChild title="View">
                              <Link to={`/knowledge-base/${article.id}`}>
                                <Eye className="w-4 h-4" />
                              </Link>
                            </Button>
                            <Button variant="ghost" size="icon" asChild title="Edit">
                              <Link to={`/knowledge-base/${article.id}/edit`}>
                                <Edit className="w-4 h-4" />
                              </Link>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} articles
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.pages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= pagination.pages - 2) {
                    pageNum = pagination.pages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className="w-8 h-8 p-0"
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={page === pagination.pages}
                onClick={() => setPage(page + 1)}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
